import * as yup from 'yup';
import i18n from 'helpers/i18n';
import messages from './messages';
import { useState, useEffect } from 'react';
import { Box } from 'components/Box';
import CheckBox from 'components/CheckBox';
import { RadioInput } from '../MetaDataCategoryForm/Question/RadioInput';
import { AgeLevel } from 'types/api/envScales/AgeLevel';
import { Assessment } from 'types/api/envScales/Assessment';
import { ObservationCycle } from 'types/api/envScales/ObservationCycle';
import { CycleMetaDataValue } from 'types/api/envScales/CycleMetaDataValue';
import { updateCycleMetaData } from 'actions/envScales/observationCycles';
import { useServerFieldError } from 'components/Measure/View/ValidationsContext';
import { ErrorMessage } from 'components/Measure/View/Styled';

interface ASIMetaDataProps {
  /** Observation Cycle. */
  cycle: ObservationCycle;

  /** CLASS Assessment. */
  assessment: Assessment;

  /** Environmental Scales Age Level. */
  ageLevel: AgeLevel;

  /** Observation ID */
  observationId: number;

  /** Meta data values */
  values: CycleMetaDataValue[];

  /** Content Choice ID - links format selections to specific content */
  contentChoiceId: number;

  /** Content Choice Name - for debugging/logging */
  contentChoiceName: string;

  /** Format Title */
  headerTitle: string;

  /** Content Question */
  question: string;

  /** Content Question */
  contentQuestion: string;

  /** Format Question */
  formatQuestion: string;

  /** Content Exposure Question */
  contentExposureQuestion: string;

  /** Content Exposure Question ID */
  contentExposureQuestionId?: number;
}

interface FormatChoice {
  [key: number]: Array<{ value: string; is_primary: boolean }>;
}

function getInitialFormatState(
  formatChoices: any[],
  cycleMetaDataValues: CycleMetaDataValue[],
  contentChoiceId: number,
  formatCategory: any
): FormatChoice {
  const initialState: FormatChoice = {};

  if (!formatCategory) {
    return initialState;
  }

  formatChoices.forEach(choice => {
    const existingValues = cycleMetaDataValues.filter(
      value =>
        value.meta_data_category_id === formatCategory.id &&
        value.meta_data_choice_id === contentChoiceId &&
        value.value &&
        choice.t_name === value.value
    );

    initialState[choice.id] = existingValues.map(value => ({
      value: value.value,
      is_primary: value.is_primary
    }));
  });

  return initialState;
}

function addValue(
  value: string,
  choiceId: number,
  currentValues: FormatChoice
): FormatChoice {
  const newValues = { ...currentValues };
  if (!newValues[choiceId]) {
    newValues[choiceId] = [];
  }

  const exists = newValues[choiceId].find(item => item.value === value);
  if (!exists) {
    newValues[choiceId] = [
      ...newValues[choiceId],
      { value, is_primary: false }
    ];
  }

  return newValues;
}

function removeValue(
  value: string,
  choiceId: number,
  currentValues: FormatChoice
): FormatChoice {
  const newValues = { ...currentValues };
  if (newValues[choiceId]) {
    newValues[choiceId] = newValues[choiceId].filter(
      item => item.value !== value
    );
  }
  return newValues;
}

/**
 * Renders metadata content specific to any Format Content form for Infant Toddler (IT) Second Edition assessments
 */
function ASIMetaData({
  cycle,
  ageLevel,
  observationId,
  contentChoiceId,
  contentChoiceName,
  headerTitle,
  contentQuestion,
  contentExposureQuestion,
  contentExposureQuestionId
}: ASIMetaDataProps) {
  const formatCategory = ageLevel.meta_data_categories?.find(
    category => category.t_name === 'format'
  );
  const formatChoices = formatCategory?.choices || [];
  const contentCategory = ageLevel.meta_data_categories?.find(
    category => category.t_name === 'content'
  );
  const [formatValues, setFormatValues] = useState<FormatChoice>(
    getInitialFormatState(
      formatChoices,
      cycle.cycle_meta_data_values,
      contentChoiceId,
      formatCategory
    )
  );
  const [metaDataAnswers, setMetaDataAnswers] = useState<any[]>(
    cycle.meta_data_answers
  );

  const checkedFormatValues = Object.values(formatValues)
    .flat()
    .map(item => item.value);

  const formatsError = useServerFieldError(
    `cycles.${cycle.cycle_number}.details.content_and_format.${contentChoiceName}.formats`,
    checkedFormatValues,
    yup.array().min(1)
  );

  const contentExposureAnswer = metaDataAnswers.find(
    answer =>
      answer.meta_data_question_id === contentExposureQuestionId &&
      cycle.id === answer.observation_cycle_id
  )?.value;

  // Optional: the block below is skipped entirely when the id is absent,
  // so a required schema here would be permanently unresolvable.
  const contentExposureError = useServerFieldError(
    `cycles.${cycle.cycle_number}.details.content_and_format.${contentChoiceName}.questions.${contentExposureQuestionId}`,
    contentExposureAnswer,
    contentExposureQuestionId ? yup.number().required() : yup.number()
  );

  useEffect(() => {
    setFormatValues(
      getInitialFormatState(
        formatChoices,
        cycle.cycle_meta_data_values,
        contentChoiceId,
        formatCategory
      )
    );
    setMetaDataAnswers(cycle.meta_data_answers);
  }, [
    cycle.cycle_meta_data_values,
    cycle.meta_data_answers,
    formatChoices,
    contentChoiceId,
    formatCategory
  ]);

  function handleCheckboxInputChange(
    event: React.ChangeEvent<HTMLInputElement>,
    categoryId: number,
    choiceId: number,
    choiceName: string
  ) {
    const value = choiceName;

    let newValues: FormatChoice;
    if (event.currentTarget.checked) {
      newValues = addValue(value, choiceId, formatValues);
    } else {
      newValues = removeValue(value, choiceId, formatValues);
    }
    setFormatValues(newValues);

    const payload = {
      meta_data: {
        meta_data_category_id: categoryId,
        meta_data_choice_id: contentChoiceId,
        values: Object.values(newValues)
          .flat()
          .map(item => ({
            value: item.value,
            is_primary: false
          }))
      }
    };

    updateCycleMetaData(observationId, cycle.id, payload).then(({ data }) => {
      setFormatValues(
        getInitialFormatState(
          formatChoices,
          data.cycle_meta_data_values,
          contentChoiceId,
          formatCategory
        )
      );
      setMetaDataAnswers(data.meta_data_answers);
    });
  }

  function handleContentExposureChange(
    event: React.ChangeEvent<HTMLInputElement>
  ) {
    if (!contentExposureQuestionId || !contentCategory) {
      return;
    }

    const value = parseInt(event.currentTarget.value);

    const tempMetaDataAnswer = {
      meta_data_question_id: contentExposureQuestionId,
      observation_cycle_id: cycle.id,
      value
    };

    let answers = metaDataAnswers.filter(
      ans =>
        ans.meta_data_question_id !== contentExposureQuestionId ||
        cycle.id !== ans.observation_cycle_id
    );
    setMetaDataAnswers([...answers, tempMetaDataAnswer]);

    const payload = {
      meta_data: {
        meta_data_category_id: contentCategory.id,
        age_level: 'IT',
        question_id: contentExposureQuestionId,
        answer: value,
        values: [
          {
            value: value,
            is_primary: false
          }
        ]
      }
    };

    updateCycleMetaData(observationId, cycle.id, payload).then(({ data }) => {
      setMetaDataAnswers(data.meta_data_answers);
    });
  }

  if (!formatCategory || formatChoices.length === 0) {
    return null;
  }

  return (
    <Box mt="50px">
      <div className="bg-[#F4F8FA] rounded-lg shadow-md border-l-4 border-blue-600 p-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-6">{headerTitle}</h2>

        <div className="mt-4 mb-8">
          <div className="font-bold text-[16px] leading-[28px] tracking-[0] text-lg mb-4">
            {contentQuestion}
            <span className="font-light text-[14px] leading-[20px] tracking-0 ml-2">
              {i18n.ft(messages.selectAll)}
            </span>
          </div>

          <div className="grid grid-cols-2 gap-x-8 gap-y-2">
            {formatChoices.map(formatChoice => {
              const isChecked = !!(
                formatValues[formatChoice.id] &&
                formatValues[formatChoice.id].find(
                  item => item.value === formatChoice.t_name
                )
              );

              return (
                <CheckBox
                  key={formatChoice.id}
                  id={`format_${formatChoice.id}`}
                  label={formatChoice.name}
                  compact={true}
                  checked={isChecked}
                  handleChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    handleCheckboxInputChange(
                      e,
                      formatCategory.id,
                      formatChoice.id,
                      formatChoice.t_name
                    );
                  }}
                />
              );
            })}
          </div>

          {formatsError && <ErrorMessage>{formatsError.message}</ErrorMessage>}
        </div>

        <div className="my-6 border-t border-2 border-white"></div>

        {contentExposureQuestion && contentExposureQuestionId && (
          <div className="mb-8">
            <div className="font-bold text-[16px] leading-[28px] tracking-[0] text-lg mb-4">
              {contentExposureQuestion}
            </div>
            <div className="grid grid-cols-3 gap-6">
              <div className="flex flex-col space-y-3">
                <label className="text-gray-700 font-medium">
                  {i18n.ft(messages.none)}
                </label>
                <RadioInput
                  name={`contentExposure_${contentExposureQuestionId}`}
                  value="1"
                  checked={
                    metaDataAnswers.find(
                      answer =>
                        answer.meta_data_question_id ===
                        contentExposureQuestionId
                    )?.value === 1
                  }
                  onChange={handleContentExposureChange}
                  error={!!contentExposureError}
                />
              </div>
              <div className="flex flex-col space-y-3">
                <label className="text-gray-700 font-medium">
                  {i18n.ft(messages.initiated)}
                </label>
                <RadioInput
                  name={`contentExposure_${contentExposureQuestionId}`}
                  value="2"
                  checked={
                    metaDataAnswers.find(
                      answer =>
                        answer.meta_data_question_id ===
                        contentExposureQuestionId
                    )?.value === 2
                  }
                  onChange={handleContentExposureChange}
                  error={!!contentExposureError}
                />
              </div>
              <div className="flex flex-col space-y-3">
                <label className="text-gray-700 font-medium">
                  {i18n.ft(messages.extended)}
                </label>
                <RadioInput
                  name={`contentExposure_${contentExposureQuestionId}`}
                  value="3"
                  checked={
                    metaDataAnswers.find(
                      answer =>
                        answer.meta_data_question_id ===
                        contentExposureQuestionId
                    )?.value === 3
                  }
                  onChange={handleContentExposureChange}
                  error={!!contentExposureError}
                />
              </div>
            </div>
            {contentExposureError && (
              <ErrorMessage>{contentExposureError.message}</ErrorMessage>
            )}
          </div>
        )}
      </div>
    </Box>
  );
}

export default ASIMetaData;
